// src/config/stripe.js - UNIFIED VERSION
import { loadStripe } from '@stripe/stripe-js';
import { COURSE_PACKAGES } from './coursePackages';  // ✅ Import from single source

// Initialize Stripe
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!stripePublishableKey) {
  throw new Error('Missing REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable');
}

// This is your Stripe publishable key
export const stripePromise = loadStripe(stripePublishableKey);

// Firebase Cloud Functions URL base
const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL || 
  'https://us-central1-arabic-ai-kids.cloudfunctions.net';

// ✅ REMOVED: STRIPE_PLANS - now using COURSE_PACKAGES directly

// ✅ NEW: Helper function to convert Firestore Timestamps for API calls
function convertTimestampForAPI(timestamp) {
  if (!timestamp) return null;
  
  // Handle Firestore Timestamp objects
  if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
    return new Date(timestamp.seconds * 1000).toISOString();
  }
  
  // Handle regular Date objects
  if (timestamp instanceof Date) {
    return timestamp.toISOString();
  }
  
  // Handle date strings
  if (typeof timestamp === 'string') {
    return new Date(timestamp).toISOString();
  }
  
  return null;
}

// Build an Error from a Cloud Function error body, carrying the `discountError`
// marker through so a page can route the message to the coupon field instead of
// showing it as a page-level payment failure.
function checkoutError(errorData, status) {
  const e = new Error(
    (errorData && (errorData.error || errorData.details)) || `HTTP error! status: ${status}`
  );
  if (errorData && errorData.discountError) e.discountError = true;
  return e;
}

export const cleanupExpiredAccount = async (userId) => {
  try {
    console.log(`🧹 Cleaning up expired account: ${userId}`);
    
    const response = await fetch(`${FUNCTIONS_BASE_URL}/cleanupExpiredAccount`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: userId
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Cleanup failed');
    }

    console.log('✅ Account cleanup successful:', result);
    
    // After successful cleanup, user will be signed out
    // since their auth account was deleted
    return result;
    
  } catch (error) {
    console.error('❌ Account cleanup failed:', error);
    throw error;
  }
};

// Create checkout session for course purchase
export const createCheckoutSession = async (userId, packageId, userEmail, quantity = 1, savePaymentMethod = false, discountCode = null, isRenewal = false, currentEndDate = null, children = null) => {
  // ADD THIS DEBUG BLOCK RIGHT HERE
  console.log('=== STRIPE.JS RECEIVED ===');
  console.log('children parameter:', children);
  console.log('children is array?', Array.isArray(children));
  console.log('children length:', children?.length);
  if (children && children.length > 0) {
    console.log('First child:', JSON.stringify(children[0], null, 2));
  }
  // END DEBUG BLOCK
  try {
    // PHASE 3: Skip packageId validation if using per-child format
    let coursePackage = null;
    
    if (!children || children.length === 0) {
      // Legacy format - validate packageId
      coursePackage = COURSE_PACKAGES[packageId];
      if (!coursePackage) {
        throw new Error(`Invalid package ID: ${packageId}`);
      }
    } else {
      // Per-child format - packageId will be in children array
      console.log('Using per-child format - skipping packageId validation');
    }

    // ✅ FIXED: Convert Firestore Timestamp to proper date format
    const convertedEndDate = convertTimestampForAPI(currentEndDate);

    if (coursePackage) {
      console.log('Creating checkout session with:', {
        userId,
        packageId, 
        packageName: coursePackage.name,
        price: coursePackage.price,
        quantity,
        savePaymentMethod,
        isRenewal,
        currentEndDate: convertedEndDate
      });
      } else {
      console.log('Creating per-child checkout session with:', {
        userId,
        childCount: children.length,
        children: children.map(c => ({ name: c.name, packageId: c.packageId })),
        isRenewal
      });
    }

    const requestBody = {
      userId,
      packageId,
      quantity,
      userEmail,
      savePaymentMethod,
      discountCode,
      isRenewal,
      currentEndDate: convertedEndDate,
      successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${window.location.origin}/complete-subscription?canceled=true`
    };

    // PHASE 3: Add children data if provided (per-child format)
    console.log('=== BEFORE ADDING CHILDREN ===');
    console.log('children parameter check:', {
      exists: !!children,
      isArray: Array.isArray(children),
      length: children?.length,
      value: children
    });
    
    if (children && Array.isArray(children) && children.length > 0) {
      requestBody.children = children;
      requestBody.format = 'per-child';
      requestBody.enrollmentType = isRenewal ? 'renewal' : 'initial_signup';
      
      console.log('=== CHILDREN ADDED TO REQUEST BODY ===');
      console.log('requestBody.children:', JSON.stringify(requestBody.children, null, 2));
      console.log('requestBody.format:', requestBody.format);
    } else {
      console.log('=== CHILDREN NOT ADDED - WHY? ===');
      console.log('children falsy?', !children);
      console.log('not array?', !Array.isArray(children));
      console.log('empty?', children?.length === 0);
    }

    console.log('📤 FINAL REQUEST BODY:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(`${FUNCTIONS_BASE_URL}/createCheckoutSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('📥 Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Backend error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (parseError) {
        errorData = { error: `HTTP ${response.status}: ${errorText}` };
      }

      throw checkoutError(errorData, response.status);
    }

    const session = await response.json();
    console.log('✅ Checkout session created successfully:', session.sessionId);
    return session;
  } catch (error) {
    console.error('❌ Error creating checkout session:', error);
    throw error;
  }
};

// Create renewal checkout session
export const createRenewalCheckoutSession = async (userId, packageId, quantity = 1, discountCode = null, currentEndDate = null) => {
  try {
    console.log('📄 Creating renewal checkout session with:', {
      userId,
      packageId,
      quantity,
      discountCode,
      currentEndDate: currentEndDate ? convertTimestampForAPI(currentEndDate) : null
    });

    // ✅ FIXED: Pass null for userEmail to let backend fetch it
    return await createCheckoutSession(
      userId, 
      packageId, 
      null, // Backend will fetch user email from Firestore
      quantity, 
      false, // savePaymentMethod handled separately for renewals
      discountCode, 
      true, // isRenewal = true
      currentEndDate
    );
  } catch (error) {
    console.error('❌ Error creating renewal checkout session:', error);
    throw error;
  }
};

/**
 * PHASE 3: Create Multi-Child Renewal Checkout
 * Renew multiple children at once with Stripe checkout
 */
export const createMultiChildRenewalCheckout = async (userId, childIds, packageId, discountCode = null) => {
  try {
    console.log('🔄 Creating multi-child renewal checkout:', {
      userId,
      childIds,
      packageId,
      childCount: childIds.length
    });

    const response = await fetch(`${FUNCTIONS_BASE_URL}/createMultiChildRenewalCheckout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        childIds,
        packageId,
        discountCode,
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/course-renewal?canceled=true`
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw checkoutError(errorData, response.status);
    }

    const session = await response.json();
    console.log('✅ Multi-child renewal checkout created:', session.sessionId);
    return session;
  } catch (error) {
    console.error('❌ Error creating multi-child renewal checkout:', error);
    throw error;
  }
};

// Renew course with saved payment method
export const renewWithSavedPayment = async (userId, packageId, quantity = 1, discountCode = null, currentEndDate = null) => {
  try {
    // ✅ FIXED: Convert Firestore Timestamp to proper date format
    const convertedEndDate = convertTimestampForAPI(currentEndDate);
    
    const response = await fetch(`${FUNCTIONS_BASE_URL}/renewCourseWithSavedPayment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        packageId,
        quantity,
        discountCode,
        currentEndDate: convertedEndDate  // ✅ Use converted ISO string
      }),
    });

    // Log response details for debugging
    console.log('Renewal response status:', response.status);
    console.log('Renewal response headers:', response.headers);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        console.error('Failed to parse error response:', parseError);
        throw new Error(`HTTP ${response.status}: Server returned invalid response`);
      }
      
      console.error('❌ Renewal failed with error:', errorData);
      throw checkoutError(errorData, response.status);
    }

    const result = await response.json();
    console.log('✅ Renewal successful:', result);
    return result;
  } catch (error) {
    console.error('❌ Error renewing with saved payment:', error);
    
    // Provide more specific error messages based on error type
    if (error.message.includes('No saved payment method')) {
      throw new Error('No saved payment method found. Please use a new payment method.');
    } else if (error.message.includes('Invalid package')) {
      throw new Error('Invalid course package selected. Please try again.');
    } else if (error.message.includes('Invalid discount code')) {
      throw new Error('The discount code you entered is invalid or expired.');
    } else if (error.message.includes('HTTP 500') || error.message.includes('Failed to process renewal')) {
      throw new Error('Server error occurred. Please try again or contact support.');
    }
    
    throw error;
  }
};

/**
 * PHASE 3: Renew Multiple Children with Saved Payment
 * Process renewal for multiple children using saved card
 */
export const renewMultipleChildrenWithSavedPayment = async (userId, childIds, packageId, discountCode = null) => {
  try {
    console.log('💳 Renewing multiple children with saved payment:', {
      userId,
      childIds,
      packageId,
      childCount: childIds.length
    });

    const response = await fetch(`${FUNCTIONS_BASE_URL}/renewMultipleChildrenWithSavedPayment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        childIds,
        packageId,
        discountCode
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw checkoutError(errorData, response.status);
    }

    const result = await response.json();
    console.log('✅ Multi-child renewal with saved payment successful:', result);
    return result;
  } catch (error) {
    console.error('❌ Error renewing multiple children with saved payment:', error);
    
    if (error.message.includes('No saved payment method')) {
      throw new Error('No saved payment method found. Please use a new payment method.');
    }
    
    throw error;
  }
};

/**
 * PHASE 3: Renew with Per-Child Packages (Saved Payment)
 * Process renewal for multiple children with individual packages using saved card
 */
export const renewWithPerChildPackages = async (userId, children, discountCode = null) => {
  try {
    console.log('Renewing with per-child packages (saved payment):', {
      userId,
      childCount: children.length,
      children: children.map(c => ({ name: c.name, packageId: c.packageId }))
    });

    const response = await fetch(`${FUNCTIONS_BASE_URL}/renewWithPerChildPackages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        children,  // Array with per-child packageIds
        discountCode,
        isRenewal: true
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw checkoutError(errorData, response.status);
    }

    const result = await response.json();
    console.log('Per-child renewal with saved payment successful:', result);
    return result;
  } catch (error) {
    console.error('Error renewing with per-child packages:', error);
    
    if (error.message.includes('No saved payment method')) {
      throw new Error('No saved payment method found. Please use a new payment method.');
    }
    
    throw error;
  }
};

/**
 * PHASE 3: Enroll Multiple Children
 * Add 1 or more new children to existing user account
 */
export const enrollMultipleChildren = async (userId, children, discountCode = null) => {
  try {
    console.log('👶 Enrolling multiple children:', {
      userId,
      childCount: children.length,
      children: children.map(c => ({ name: c.name, packageId: c.packageId }))
    });

    const response = await fetch(`${FUNCTIONS_BASE_URL}/enrollMultipleChildren`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        children,
        discountCode,
        successUrl: `${window.location.origin}/payment-success?enrollment=true&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/account?enrollment_canceled=true`
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw checkoutError(errorData, response.status);
    }

    const session = await response.json();
    console.log('✅ Children enrollment checkout created:', session.sessionId);
    return session;
  } catch (error) {
    console.error('❌ Error enrolling children:', error);
    throw error;
  }
};

// Validate discount code.
// subtotalCents (optional): the REAL basket total in cents — the sum of the
// actually-selected packages. Needed so the server can check a promotion code's
// restrictions.minimum_amount against a mixed-package basket, which packageId ×
// quantity cannot represent. Omit it and the server falls back to price×quantity.
export const validateDiscountCode = async (discountCode, packageId, quantity = 1, subtotalCents = null) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/validateDiscountCode`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        discountCode,
        packageId,
        quantity,
        subtotalCents
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error validating discount code:', error);
    throw error;
  }
};

// Verify payment session
export const verifyPaymentSession = async (sessionId, userId) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/verifyPaymentSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sessionId,
        userId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error verifying payment session:', error);
    throw error;
  }
};

// Create customer portal session
export const createCustomerPortal = async (userId) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/createCustomerPortal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const { url } = await response.json();
    return url;
  } catch (error) {
    console.error('Error creating customer portal session:', error);
    throw error;
  }
};

// Remove saved payment method
export const removeSavedPaymentMethod = async (userId) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/removeSavedPaymentMethod`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error removing saved payment method:', error);
    throw error;
  }
};

// Get course/user status
export const getCourseStatus = async (userId) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/getCourseStatus`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting course status:', error);
    throw error;
  }
};

// Legacy function for backward compatibility
export const cancelSubscription = async (userId, cancelAtPeriodEnd = true) => {
  // This is now a no-op since we don't have subscriptions anymore
  // But keeping it for any existing code that might call it
  console.warn('cancelSubscription called but courses are now one-time payments');
  return { success: true, message: 'No subscription to cancel - courses are one-time payments' };
};

// Legacy function for backward compatibility  
export const reactivateSubscription = async (userId) => {
  // This is now a no-op since we don't have subscriptions anymore
  console.warn('reactivateSubscription called but courses are now one-time payments');
  return { success: true, message: 'No subscription to reactivate - use renewal instead' };
};

// Legacy function for backward compatibility
export const getSubscriptionStatus = async (userId) => {
  // Redirect to course status instead
  return await getCourseStatus(userId);
};

